import React, { useState } from "react";
import { Link, NavLink, useNavigate, useParams } from "react-router-dom";

import { WorkbookDetailsDocument } from "../Workbook/gql/Query.generated";
import { WorkbooksDocument } from "../gql/Query.generated";
import { WorkbookType, WorkbookVariantType } from "../Workbook";
import { useDeleteWorkbookVariantMutation } from "./gql/Mutation.variant.generated";
import { useDeleteWorkbookMutation } from "./gql/Mutation.workbook.generated";

import { useRoutes } from "utils/routes";
import Popover from "ui/Popover";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCodeCommit,
  faCodePullRequestDraft,
  faFingerprint,
  faPlus,
} from "@fortawesome/pro-solid-svg-icons";
import { faEdit, faTrashAlt } from "@fortawesome/pro-light-svg-icons";
import QueryTags, { QueryTagType } from "features/Queries/QueryTags";
import Button from "ui/Button";
import moment from "moment";
import { formatTimestampLong } from "utils/format";
import WorkbookNameEditPanel from "../WorkbookNameEditPanel";
import classNames from "classnames";
import Badge from "ui/Badge";
import VariantNameEditPanel from "../VariantNameEditPanel";
import InsightTile, { NodeInsightType, variantInsights } from "../InsightTile";
import { faDiagramSubtask } from "@fortawesome/pro-regular-svg-icons";

export const WorkbookSidebar = ({
  workbook,
  permittedToCreateWorkbooks,
}: {
  workbook: WorkbookType;
  permittedToCreateWorkbooks: boolean;
}) => {
  const { databaseId } = useParams();
  const { databaseWorkbooks, databaseQuery } = useRoutes();
  const [showEdit, setShowEdit] = useState(false);
  const navigate = useNavigate();
  const [DeleteWorkbook] = useDeleteWorkbookMutation();

  const handleDismissEdit = () => {
    setShowEdit(false);
  };

  const handleEditWorkbook = () => {
    setShowEdit(true);
  };

  const handleDeleteWorkbook = () => {
    if (window.confirm(`Delete workbook "${workbook.name}"?`)) {
      DeleteWorkbook({
        variables: { workbookId: workbook.id },
        refetchQueries: [
          {
            query: WorkbooksDocument,
            variables: {
              databaseId: workbook.databaseId,
            },
          },
        ],
        awaitRefetchQueries: true,
        onCompleted: () => {
          navigate(databaseWorkbooks(databaseId));
        },
      });
    }
  };
  return (
    <div className="pb-16">
      <SidebarNav
        workbook={workbook}
        permittedToCreateWorkbooks={permittedToCreateWorkbooks}
      />
      <hr />
      <VariantInsights
        workbook={workbook}
        variant={workbook.baselineQuery}
        baseline
      />
      <div className="leading-7 mt-0 font-medium">Baseline Query</div>
      <div className="mb-2">
        {workbook.baselineQuery.query ? (
          <Link to={databaseQuery(databaseId, workbook.baselineQuery.query.id)}>
            #{workbook.baselineQuery.query.id}
          </Link>
        ) : (
          <span className="italic text-[#606060]">
            No match with existing queries
          </span>
        )}
      </div>
      <div className="leading-7 mt-0 font-medium">Owner</div>
      <div className="mb-2">{workbook.user.fullname || "Deleted User"}</div>
      <div className="leading-7 mt-0 font-medium">Workbook description</div>
      <div className="mb-2">
        {workbook.description ?? (
          <span className="italic text-[#606060]">No description</span>
        )}
      </div>
      {workbook.channel === "mcp" && (
        <>
          <div className="leading-7 mt-0 font-medium">Created via</div>
          <div className="mb-2">MCP</div>
        </>
      )}
      <div className="leading-7 mt-0 font-medium">Last edit</div>
      <div className="mb-2">
        {formatTimestampLong(moment.unix(workbook.lastActivityAt))}
      </div>
      <hr />
      <div className="flex flex-col gap-3 items-start mb-6">
        <Button bare onClick={handleEditWorkbook} variant="link">
          <FontAwesomeIcon icon={faEdit} /> Edit name and description
        </Button>
        <Button bare onClick={handleDeleteWorkbook} className="!text-[#d43f3a]">
          <FontAwesomeIcon icon={faTrashAlt} /> Delete workbook
        </Button>
      </div>
      {showEdit && (
        <WorkbookEditPanel workbook={workbook} onDismiss={handleDismissEdit} />
      )}
    </div>
  );
};

function WorkbookEditPanel({
  workbook,
  onDismiss,
}: {
  workbook: WorkbookType;
  onDismiss: () => void;
}) {
  const [name, setName] = useState(workbook.name);
  const [description, setDescription] = useState(workbook.description);

  return (
    <WorkbookNameEditPanel
      onDismiss={onDismiss}
      name={name}
      description={description}
      setName={setName}
      setDescription={setDescription}
      workbook={workbook}
    />
  );
}

const SidebarNav = ({
  workbook,
  permittedToCreateWorkbooks,
}: {
  workbook: WorkbookType;
  permittedToCreateWorkbooks: boolean;
}) => {
  const { databaseId } = useParams();
  const {
    databaseWorkbookVariants,
    databaseWorkbookVariant,
    databaseWorkbookVariantNew,
  } = useRoutes();
  const [deleteWorkbookVariant] = useDeleteWorkbookVariantMutation();
  const navigate = useNavigate();

  const menuList: {
    to: string;
    variantId: string;
    title: string;
    icon: React.ReactElement;
    end: boolean;
    draft?: boolean;
  }[] = [
    {
      to: databaseWorkbookVariants(databaseId, workbook.id),
      variantId: "0",
      title: "All Query Plans",
      icon: <FontAwesomeIcon icon={faDiagramSubtask} />,
      end: true,
    },
    {
      to: databaseWorkbookVariant(
        databaseId,
        workbook.id,
        workbook.baselineQuery.id,
      ),
      variantId: workbook.baselineQuery.id,
      title: "Baseline",
      icon: <FontAwesomeIcon icon={faCodeCommit} />,
      end: false,
    },
  ];
  workbook.variants.forEach((WorkbookVariant) => {
    menuList.push({
      to: databaseWorkbookVariant(databaseId, workbook.id, WorkbookVariant.id),
      variantId: WorkbookVariant.id,
      title: WorkbookVariant.name,
      icon: <FontAwesomeIcon icon={faCodePullRequestDraft} />,
      end: false,
      draft: WorkbookVariant.draft,
    });
  });
  // sort menu list by variant ID
  const sortedMenuList = menuList.sort(
    (a, b) => Number(a.variantId) - Number(b.variantId),
  );

  return (
    <div className="font-medium flex flex-col gap-2">
      <div>
        <Popover
          wrapperClassName="w-full"
          content={
            !permittedToCreateWorkbooks && (
              <div>
                You are not allowed to create new query variants - you do not
                have the <strong>Tune Queries</strong> permission on this
                database.
              </div>
            )
          }
        >
          <Link
            to={databaseWorkbookVariantNew(databaseId, workbook.id)}
            className={classNames(
              "btn btn-success w-full !px-4 !py-2.5 !font-medium",
              permittedToCreateWorkbooks
                ? ""
                : "pointer-events-none opacity-[.65]",
            )}
          >
            <FontAwesomeIcon icon={faPlus} className="mr-1" /> New Query Variant
          </Link>
        </Popover>
      </div>
      {sortedMenuList.map((menu, idx) => {
        return (
          <NavLink
            to={menu.to}
            className={({ isActive }) =>
              isActive
                ? "py-2 px-2.5 bg-[#F9FAFB] rounded-md border border-[#E8E8EE] text-[#19152C] hover:text-[#19152C]"
                : "py-2 px-2.5 rounded-md border border-transparent text-[#606060] hover:text-[#19152C] hover:bg-[#F9FAFB]"
            }
            end={menu.end}
            key={`menu${idx}`}
          >
            <div className="flex gap-2">
              <div className="flex-auto flex gap-2 items-center min-w-0">
                {menu.icon}
                <span className="flex-1 truncate min-w-0" title={menu.title}>
                  {menu.title}
                </span>
                {menu.draft && <Badge>Draft</Badge>}
              </div>
              {menu.variantId !== "0" &&
                menu.variantId !== workbook.baselineQuery.id && (
                  <Button
                    bare
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (window.confirm(`Delete variant "${menu.title}"?`)) {
                        deleteWorkbookVariant({
                          variables: { variantId: menu.variantId },
                          refetchQueries: [
                            {
                              query: WorkbookDetailsDocument,
                              variables: {
                                workbookId: workbook.id,
                                databaseId: workbook.databaseId,
                              },
                            },
                          ],
                          awaitRefetchQueries: true,
                          onCompleted: () => {
                            navigate(
                              databaseWorkbookVariants(databaseId, workbook.id),
                            );
                          },
                        });
                      }
                    }}
                    title="Delete"
                  >
                    <FontAwesomeIcon
                      icon={faTrashAlt}
                      className="text-[#CA1515]"
                    />
                  </Button>
                )}
            </div>
          </NavLink>
        );
      })}
    </div>
  );
};

const WorkbookVariantSidebar = ({
  workbook,
  variant,
  permittedToCreateWorkbooks,
}: {
  workbook: WorkbookType;
  variant?: WorkbookVariantType;
  permittedToCreateWorkbooks: boolean;
}) => {
  const { databaseId } = useParams();
  const { databaseQuery, databaseWorkbookVariants } = useRoutes();
  const navigate = useNavigate();
  const [showEdit, setShowEdit] = useState(false);
  const focusedVariant = variant || workbook.baselineQuery;
  const baseline = focusedVariant.id === workbook.baselineQuery.id;
  const [deleteWorkbookVariant] = useDeleteWorkbookVariantMutation();

  const handleDismissEdit = () => {
    setShowEdit(false);
  };

  const handleEditVariant = () => {
    setShowEdit(true);
  };

  const handleDeleteVariant = () => {
    if (window.confirm(`Delete variant "${variant.name}"?`)) {
      deleteWorkbookVariant({
        variables: { variantId: variant.id },
        refetchQueries: [
          {
            query: WorkbookDetailsDocument,
            variables: {
              workbookId: workbook.id,
              databaseId: workbook.databaseId,
            },
          },
        ],
        awaitRefetchQueries: true,
        onCompleted: () => {
          navigate(databaseWorkbookVariants(databaseId, workbook.id));
        },
      });
    }
  };

  const queryTags: QueryTagType[] = [];
  if (focusedVariant.query) {
    queryTags.push(
      ...[
        {
          id: "fingerprint",
          key: (
            <Popover
              content="A query fingerprint represents the abstracted form of a query and enables the grouping of similar queries together. This value is calculated using the pg_query library."
              popupClassName="!text-[12px]"
            >
              <FontAwesomeIcon
                icon={faFingerprint}
                className="text-[#666] text-[10px]"
              />{" "}
              fingerprint
            </Popover>
          ),
          value: focusedVariant.queryFingerprint,
        },
      ],
    );
  }

  return (
    <div className="pb-16">
      <SidebarNav
        workbook={workbook}
        permittedToCreateWorkbooks={permittedToCreateWorkbooks}
      />
      <hr />
      <VariantInsights
        workbook={workbook}
        variant={focusedVariant}
        baseline={baseline}
      />
      <div className="leading-7 mt-0 font-medium">Query</div>
      <div className="mb-3">
        {focusedVariant.query ? (
          <Link to={databaseQuery(databaseId, focusedVariant.query.id)}>
            #{focusedVariant.query.id}
          </Link>
        ) : (
          <span className="italic text-[#606060]">
            No match with existing queries
          </span>
        )}
      </div>
      <div className="leading-7 mt-0 font-medium">Query tags</div>
      <div className="mb-3">
        {queryTags.length > 0 ? (
          <QueryTags tags={queryTags} />
        ) : (
          <span className="italic text-[#606060]">No query tags</span>
        )}
      </div>
      {!baseline && (
        <>
          <div className="leading-7 mt-0 font-medium">Variant description</div>
          <div className="mb-2">
            {focusedVariant.description ?? (
              <span className="italic text-[#606060]">No description</span>
            )}
          </div>
        </>
      )}
      <hr />
      {!baseline && (
        <div className="flex flex-col gap-3 items-start mb-6">
          <Button bare onClick={handleEditVariant} variant="link">
            <FontAwesomeIcon icon={faEdit} /> Edit variant name and description
          </Button>
          <Button
            bare
            onClick={handleDeleteVariant}
            className="!text-[#d43f3a]"
          >
            <FontAwesomeIcon icon={faTrashAlt} /> Remove variant
          </Button>
        </div>
      )}
      {showEdit && (
        <VariantEditPanel
          variant={focusedVariant}
          onDismiss={handleDismissEdit}
        />
      )}
    </div>
  );
};

function VariantEditPanel({
  variant,
  onDismiss,
}: {
  variant: WorkbookVariantType;
  onDismiss: () => void;
}) {
  const [name, setName] = useState(variant.name);
  const [description, setDescription] = useState(variant.description);

  return (
    <VariantNameEditPanel
      onDismiss={onDismiss}
      name={name}
      description={description}
      setName={setName}
      setDescription={setDescription}
      variant={variant}
    />
  );
}

const VariantInsights = ({
  workbook,
  variant,
  baseline,
}: {
  workbook: WorkbookType;
  variant: WorkbookVariantType;
  baseline?: boolean;
}) => {
  const { databaseId } = useParams();
  const { databaseWorkbookVariantNew, databaseIssue } = useRoutes();
  const insights = variantInsights(variant);

  return (
    <>
      <div className="leading-7 font-medium">Query Advisor</div>
      {variant.appliedInsights?.map(
        (i: { [x: string]: string }, idx: number) => {
          const ins = {
            type: i["type"],
            insight: { id: i["node_id"], type: i["type"] },
            resultId: i["result_id"],
            variantId: i["variant_id"],
            resultFingerprint: i["result_fingerprint"],
            applied: true,
          } as unknown as NodeInsightType;
          return (
            <InsightTile
              workbookId={workbook.id}
              key={`applied-insight-${idx}`}
              insight={ins}
              hideDescription
            />
          );
        },
      )}
      {insights.length > 0 ? (
        insights.map((ins, idx) => {
          return (
            <InsightTile
              workbookId={workbook.id}
              key={`insight-${idx}`}
              insight={ins}
              linkText="New Variant"
              linkTo={databaseWorkbookVariantNew(databaseId, workbook.id, {
                search: {
                  apply: String(ins.resultId),
                },
              })}
            />
          );
        })
      ) : (
        <div className="mb-2">
          {baseline && workbook.issueId ? (
            <>
              The insight reported in the linked issue was not reproducible with
              this workbook. See{" "}
              <a
                href="https://pganalyze.com/docs/query-advisor/from-insight-to-conclusion#understanding-when-insights-may-not-reproduce"
                target="_blank"
              >
                the documentation
              </a>{" "}
              for why this may occur.
            </>
          ) : (
            `No ${baseline ? "" : "new"} insights found`
          )}
        </div>
      )}
      {workbook.issueId && (
        <div className="mb-2">
          Workbook created from Issue{" "}
          <Link
            to={databaseIssue(databaseId, "advisor_insight", workbook.issueId)}
          >
            #{workbook.issueId}
          </Link>
        </div>
      )}
      <hr />
    </>
  );
};

export default WorkbookVariantSidebar;
